import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ArtistRemoveItem,
  ArtistRemoveItemMutation,
  ArtistRemoveItemMutationVariables,
  ArtistUnuseTicketsMutation,
  ArtistUnuseTicketsMutationVariables,
  ArtistUseTicketsMutation,
  ArtistUseTicketsMutationVariables,
  ConsoleItemDetailQuery,
  ConsoleItemDetailQueryVariables,
  ItemDescriptionFragmentDoc,
  PublishItem,
  PublishItemMutation,
  PublishItemMutationVariables,
  SalesStatus,
  SerialItemOwnersFragmentDoc,
  StartSellingItem,
  StartSellingItemMutation,
  StartSellingItemMutationVariables,
  StopSellingItem,
  StopSellingItemMutation,
  StopSellingItemMutationVariables,
  TicketStatusDetailFragmentDoc,
  UnpublishItem,
  UnpublishItemMutation,
  UnpublishItemMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { useAnalytics } from '@/lib/analytics'

import { Action, State } from './template'

type TicketFilterType = 'general' | 'used' | 'unused'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showError, showMessage } = useMessage()
  const { user } = useCurrentUser()
  const { router, routerPages } =
    useRouter<{ itemId: string; ticketOpen?: string }>()
  const itemId = router.query.itemId
  const { data, networkStatus, fetchMore, refetch } = useQuery<
    ConsoleItemDetailQuery,
    ConsoleItemDetailQueryVariables
  >(QUERY, {
    variables: { id: itemId },
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })
  const { t } = useTranslation('consoleItemDetail')
  const { analytics } = useAnalytics()

  const [ticketFilter, setTicketFilter] = useState<TicketFilterType>('general')
  const [publishItem] = useMutation<
    PublishItemMutation,
    PublishItemMutationVariables
  >(PublishItem)

  const [unpublishItem] = useMutation<
    UnpublishItemMutation,
    UnpublishItemMutationVariables
  >(UnpublishItem)

  const [startSellingItem] = useMutation<
    StartSellingItemMutation,
    StartSellingItemMutationVariables
  >(StartSellingItem)

  const [stopSellingItem] = useMutation<
    StopSellingItemMutation,
    StopSellingItemMutationVariables
  >(StopSellingItem)

  const [artistRemoveItem] = useMutation<
    ArtistRemoveItemMutation,
    ArtistRemoveItemMutationVariables
  >(ArtistRemoveItem)

  const [artistUseTicket] = useMutation<
    ArtistUseTicketsMutation,
    ArtistUseTicketsMutationVariables
  >(ARTIST_USE_TICKETS)

  const [artistUnuseTicket] = useMutation<
    ArtistUnuseTicketsMutation,
    ArtistUnuseTicketsMutationVariables
  >(ARTIST_UNUSE_TICKETS)

  return {
    state: {
      artistId: user?.uid ?? '',
      loading: networkStatus === NetworkStatus.loading,
      refetchingSerial: networkStatus === NetworkStatus.fetchMore,
      hasNextSerialPage: !!data?.item?.serials?.pageInfo?.hasNextPage,
      item: data?.item ?? null,
      ticketOpen: router.query.ticketOpen,
      ticketFilter,
    },
    actions: {
      async onToggleActive(id, isActive) {
        if (isActive) {
          await unpublishItem({
            variables: { id },
            optimisticResponse() {
              return {
                unpublishItem: {
                  id,
                  isPublished: false,
                  __typename: 'Item',
                },
              }
            },
          }).catch(showError)
          return
        }
        await publishItem({
          variables: { id },
          optimisticResponse() {
            return {
              publishItem: {
                id,
                isPublished: true,
                __typename: 'Item',
              },
            }
          },
        }).catch(showError)
      },
      async onToggleSelling(id, isSelling) {
        if (isSelling) {
          await stopSellingItem({
            variables: { id },
            optimisticResponse() {
              return {
                stopSellingItem: {
                  id,
                  salesStatus: SalesStatus.Stopped,
                  __typename: 'Item',
                },
              }
            },
          }).catch(showError)
          return
        }
        await startSellingItem({
          variables: { id },
          optimisticResponse() {
            return {
              startSellingItem: {
                id,
                salesStatus: SalesStatus.Active,
                __typename: 'Item',
              },
            }
          },
        }).catch(showError)
      },
      async onRemove() {
        await artistRemoveItem({ variables: { id: itemId } })
        router.goBack(routerPages.ConsoleItems)
      },
      async onSettingAvatar() {},
      async onFetchMoreSerial() {
        await fetchMore({
          variables: {
            id: itemId,
            pageInput: {
              cursor: data?.item?.serials?.pageInfo.endCursor,
            },
          },
        })
      },
      async onFilterTicket(filterType: TicketFilterType) {
        if (filterType === ticketFilter) return
        setTicketFilter(filterType)

        refetch({
          filter:
            filterType === 'general'
              ? undefined
              : {
                  ticketUsed: filterType === 'used',
                },
        })
      },
      // チケットアイテムの使用状況を更新する
      async onToggleTicketUsed(serialId, isUsed, throughTicketName?: string) {
        if (isUsed) {
          // チケットを未使用に戻す理
          analytics?.track('click_unuseTicket', {
            applicationKey: config.applicationKey,
            eventId: data?.item?.collection?.event?.id,
            collectionId: data?.item?.collection?.id,
            asArtist: true,
          })

          await artistUnuseTicket({
            variables: {
              inputs: {
                items: [{ serialIds: [serialId], itemId, throughTicketName }],
              },
            },
          })
            .then(() =>
              showMessage(
                t('UnuseTicketSuccess', '使用状況を未使用に変更しました。')
              )
            )
            .catch(showError)

          return
        }
        // チケットを使用する処理
        analytics?.track('click_useTicket', {
          applicationKey: config.applicationKey,
          eventId: data?.item?.collection?.event?.id,
          collectionId: data?.item?.collection?.id,
          asArtist: true,
        })

        await artistUseTicket({
          variables: {
            inputs: {
              items: [{ itemId, serialIds: [serialId], throughTicketName }],
            },
          },
        })
          .then(() => showMessage(t('使用状況を使用済みに変更しました。')))
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleItemDetail(
    $id: ID!
    $pageInput: PageInput
    $filter: SerialFilter
    $skuId: String
  ) {
    item(id: $id) {
      ...ItemDescription
      salesStatus
      salesStartedAt
      salesEndedAt
      salesCondition @client
      isPublished
      skuSettings {
        keyName
        units {
          id
        }
      }
      isPremium
      status @client
      formattedPrice @client
      usedNumber
      pendingPurchaseNumber
      serials(pageInput: $pageInput, filter: $filter) {
        ...SerialItemOwners
      }
      collection {
        id
        isEventPickup
      }
    }
  }
  ${ItemDescriptionFragmentDoc}
  ${SerialItemOwnersFragmentDoc}
`

const ARTIST_USE_TICKETS = gql`
  mutation artistUseTickets($inputs: UseTicketsInput!) {
    artistUseTickets(inputs: $inputs) {
      id
      obtainedDate
      ticketStatus {
        ...TicketStatusDetail
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`

const ARTIST_UNUSE_TICKETS = gql`
  mutation artistUnuseTickets($inputs: UnuseTicketsInput!) {
    artistUnuseTickets(inputs: $inputs) {
      id
      obtainedDate
      ticketStatus {
        ...TicketStatusDetail
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`
